import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Loader2, 
  TrendingUp, 
  TrendingDown,
  Minus,
  Building2,
  MapPin,
  Calendar,
  User,
  Wallet,
  BarChart3,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${(value * 100).toFixed(2)}%`;
};

// Compare indicator component - returns styled value based on comparison
const getCompareStyle = (value, values, type) => {
  if (value === null || value === undefined) return { colorClass: 'text-slate-700 dark:text-slate-300', Icon: null };
  
  const validValues = values.filter(v => v !== null && v !== undefined);
  if (validValues.length < 2) return { colorClass: 'text-slate-700 dark:text-slate-300', Icon: null };
  
  const max = Math.max(...validValues);
  const min = Math.min(...validValues);
  const allEqual = max === min;
  
  if (allEqual) return { colorClass: 'text-slate-700 dark:text-slate-300', Icon: null };
  
  const isMax = value === max;
  const isMin = value === min;
  
  if (type === 'higher-better') {
    if (isMax) return { colorClass: 'text-emerald-600 dark:text-emerald-400 font-semibold', Icon: TrendingUp };
    if (isMin) return { colorClass: 'text-red-600 dark:text-red-400', Icon: TrendingDown };
  } else if (type === 'lower-better') {
    if (isMin) return { colorClass: 'text-emerald-600 dark:text-emerald-400 font-semibold', Icon: TrendingUp };
    if (isMax) return { colorClass: 'text-red-600 dark:text-red-400', Icon: TrendingDown };
  }
  
  return { colorClass: 'text-slate-700 dark:text-slate-300', Icon: null };
};

// Verdict badge component
const VerdictBadge = ({ verdict }) => {
  const config = {
    GO: { 
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
      icon: CheckCircle2
    },
    'NO GO': { 
      className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
      icon: XCircle
    },
    "A L'ETUDE": { 
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
      icon: AlertCircle
    },
  };
  
  const cfg = config[verdict] || config["A L'ETUDE"];
  const Icon = cfg.icon;
  
  return (
    <Badge className={`${cfg.className} gap-1`}>
      <Icon className="w-3 h-3" />
      {verdict || '-'}
    </Badge>
  );
};

// Comparison row component
const CompareRow = ({ label, values, type = 'higher-better', format: formatFn = formatCurrency, icon: RowIcon }) => {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800">
      <td className="py-3 px-4 font-medium text-slate-600 dark:text-slate-400">
        <div className="flex items-center gap-2">
          {RowIcon && <RowIcon className="w-4 h-4" />}
          {label}
        </div>
      </td>
      {values.map((value, index) => {
        const { colorClass, Icon } = getCompareStyle(value, values, type);
        const displayValue = value !== null && value !== undefined ? formatFn(value) : '-';
        
        return (
          <td key={index} className="py-3 px-4 text-center">
            <div className={`flex items-center justify-center gap-1 ${colorClass}`}>
              {Icon && <Icon className="w-4 h-4" />}
              <span>{displayValue}</span>
            </div>
          </td>
        );
      })}
    </tr>
  );
};

export default function SimulationComparePage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const dateLocale = language === 'fr' ? fr : enUS;
  
  // Get simulation IDs from URL
  const simulationIds = searchParams.get('ids')?.split(',') || [];
  
  useEffect(() => {
    if (simulationIds.length < 2) {
      toast.error(language === 'fr' ? 'Sélectionnez au moins 2 simulations' : 'Select at least 2 simulations');
      navigate('/history');
      return;
    }
    
    fetchSimulations();
  }, [simulationIds.join(',')]);
  
  const fetchSimulations = async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        simulationIds.map(id => api().get(`/simulations/${id}`))
      );
      setSimulations(responses.map(r => r.data));
    } catch (error) {
      console.error('Failed to fetch simulations:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load simulations');
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      </AppLayout>
    );
  }
  
  // Extract data for comparison
  const getValues = (path) => simulations.map(sim => {
    const parts = path.split('.');
    let value = sim;
    for (const part of parts) {
      value = value?.[part];
    }
    return value;
  });
  
  return (
    <AppLayout>
      <div className="space-y-6" data-testid="compare-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/history')}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Retour à l\'historique' : 'Back to history'}
            </Button>
            <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
              {language === 'fr' ? 'Comparateur de Simulations' : 'Simulation Comparator'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {language === 'fr' 
                ? `Comparaison de ${simulations.length} études CAPEX`
                : `Comparing ${simulations.length} CAPEX studies`}
            </p>
          </div>
        </div>
        
        {/* Project Headers */}
        <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${simulations.length}, 1fr)` }}>
          <div></div>
          {simulations.map((sim, index) => (
            <Card 
              key={sim.id} 
              className={`border-2 ${index === 0 ? 'border-blue-500' : index === 1 ? 'border-purple-500' : 'border-amber-500'}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-amber-500'}`} />
                  <CardTitle className="text-lg">{sim.input_data?.general_info?.project_name || '-'}</CardTitle>
                </div>
                <div className="space-y-1 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {sim.input_data?.general_info?.retailer_name || '-'}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {sim.input_data?.general_info?.city || '-'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {sim.created_at ? format(new Date(sim.created_at), 'dd/MM/yyyy', { locale: dateLocale }) : '-'}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <VerdictBadge verdict={sim.verdict} />
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Financial Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              {language === 'fr' ? 'Données Financières' : 'Financial Data'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-slate-600 dark:text-slate-400 w-48">
                      {language === 'fr' ? 'Indicateur' : 'Indicator'}
                    </th>
                    {simulations.map((sim, index) => (
                      <th key={sim.id} className="py-3 px-4 text-center font-semibold">
                        <div className="flex items-center justify-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-amber-500'}`} />
                          {sim.input_data?.general_info?.project_name?.substring(0, 20) || `Sim ${index + 1}`}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <CompareRow 
                    label={language === 'fr' ? 'CA Potentiel HT (Moy.)' : 'Potential Revenue (Avg.)'}
                    values={getValues('ca_potential_ht')}
                    type="higher-better"
                    icon={TrendingUp}
                  />
                  <CompareRow 
                    label="Hypothèse 1 (CA HT)"
                    values={simulations.map(s => s.hypothesis_1?.ca_ht_annual)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="Hypothèse 2 (CA HT)"
                    values={simulations.map(s => s.hypothesis_2?.ca_ht_annual)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="Hypothèse 3 (CA HT)"
                    values={simulations.map(s => s.hypothesis_3?.ca_ht_annual)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="CAPEX Total"
                    values={getValues('total_capex')}
                    type="lower-better"
                    icon={Wallet}
                  />
                  <CompareRow 
                    label="Commission"
                    values={simulations.map(s => s.input_data?.contract_info?.negotiated_commission_rate)}
                    type="lower-better"
                    format={formatPercent}
                  />
                  <CompareRow 
                    label="Cluster"
                    values={getValues('cluster_used')}
                    type="neutral"
                    format={(v) => v || '-'}
                  />
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* P&L Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              {language === 'fr' ? 'Compte de Résultat (P&L)' : 'Profit & Loss (P&L)'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-slate-600 dark:text-slate-400 w-48">
                      {language === 'fr' ? 'Indicateur' : 'Indicator'}
                    </th>
                    {simulations.map((sim, index) => (
                      <th key={sim.id} className="py-3 px-4 text-center font-semibold">
                        <div className="flex items-center justify-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-amber-500'}`} />
                          {sim.input_data?.general_info?.project_name?.substring(0, 20) || `Sim ${index + 1}`}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <CompareRow 
                    label="Gross Profit Y1"
                    values={simulations.map(s => s.pl_projections?.gross_profit_y1)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="EBITDA Y1"
                    values={simulations.map(s => s.pl_projections?.ebitda_y1)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="Operating Profit Y1"
                    values={simulations.map(s => s.pl_projections?.operating_profit_y1)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="Net Profit Y1"
                    values={simulations.map(s => s.pl_projections?.net_profit_y1)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="Direct Labor Y1"
                    values={simulations.map(s => s.pl_projections?.direct_labor_y1)}
                    type="lower-better"
                  />
                  <CompareRow 
                    label="Food Cost Rate"
                    values={getValues('food_cost_rate_used')}
                    type="lower-better"
                    format={formatPercent}
                  />
                  <CompareRow 
                    label="Other Cost Rate"
                    values={getValues('other_cost_rate_used')}
                    type="lower-better"
                    format={formatPercent}
                  />
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* Performance Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              {language === 'fr' ? 'Performance & Rentabilité' : 'Performance & Profitability'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-slate-600 dark:text-slate-400 w-48">
                      {language === 'fr' ? 'Indicateur' : 'Indicator'}
                    </th>
                    {simulations.map((sim, index) => (
                      <th key={sim.id} className="py-3 px-4 text-center font-semibold">
                        <div className="flex items-center justify-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-amber-500'}`} />
                          {sim.input_data?.general_info?.project_name?.substring(0, 20) || `Sim ${index + 1}`}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <CompareRow 
                    label={language === 'fr' ? 'Payback (mois)' : 'Payback (months)'}
                    values={getValues('payback_months')}
                    type="lower-better"
                    format={(v) => v ? `${v.toFixed(1)} mois` : '-'}
                    icon={Clock}
                  />
                  <CompareRow 
                    label={language === 'fr' ? 'Mois en rouge' : 'Red months'}
                    values={getValues('red_months_count')}
                    type="lower-better"
                    format={(v) => v !== null && v !== undefined ? v.toString() : '-'}
                  />
                  <CompareRow 
                    label="Verdict"
                    values={getValues('verdict')}
                    type="neutral"
                    format={(v) => v || '-'}
                  />
                  <CompareRow 
                    label={language === 'fr' ? 'Cumul Cash Y1' : 'Cash Cumulative Y1'}
                    values={simulations.map(s => s.pl_projections?.cumulative_cash_y1)}
                    type="higher-better"
                  />
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* Store Data Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              {language === 'fr' ? 'Données Magasin' : 'Store Data'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-slate-600 dark:text-slate-400 w-48">
                      {language === 'fr' ? 'Indicateur' : 'Indicator'}
                    </th>
                    {simulations.map((sim, index) => (
                      <th key={sim.id} className="py-3 px-4 text-center font-semibold">
                        <div className="flex items-center justify-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-amber-500'}`} />
                          {sim.input_data?.general_info?.project_name?.substring(0, 20) || `Sim ${index + 1}`}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <CompareRow 
                    label="CA Hors Carburant"
                    values={simulations.map(s => s.input_data?.financial_data?.annual_ca_hors_carburant)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="CA Alimentaire"
                    values={simulations.map(s => s.input_data?.financial_data?.annual_ca_alimentaire)}
                    type="higher-better"
                  />
                  <CompareRow 
                    label="Fréquentation"
                    values={simulations.map(s => s.input_data?.financial_data?.annual_frequentation)}
                    type="higher-better"
                    format={(v) => v ? v.toLocaleString('fr-FR') : '-'}
                  />
                  <CompareRow 
                    label={language === 'fr' ? 'Surface (m²)' : 'Surface (sqm)'}
                    values={simulations.map(s => s.input_data?.general_info?.store_surface_m2)}
                    type="neutral"
                    format={(v) => v ? `${v.toLocaleString('fr-FR')} m²` : '-'}
                  />
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* Action Buttons */}
        <div className="flex justify-center gap-4 pt-4">
          {simulations.map((sim, index) => (
            <Button
              key={sim.id}
              variant="outline"
              onClick={() => navigate(`/simulation/${sim.id}`)}
              className={`${index === 0 ? 'border-blue-500 text-blue-600 hover:bg-blue-50' : index === 1 ? 'border-purple-500 text-purple-600 hover:bg-purple-50' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}`}
            >
              <Eye className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Voir' : 'View'} {sim.input_data?.general_info?.project_name?.substring(0, 15) || `Sim ${index + 1}`}
            </Button>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
